// CONTRACT:C2-RENDER-ENGINE.1.0
/** RenderEngine implementation — the imperative core that wires the data plane
 *  (entity-store / layer-manager / time) to a GPU backend.
 *
 *  NON-NEGOTIABLE (## Behavioral Contracts): React stays OUT of the render loop.
 *  This class exposes NO React; callers drive it via method calls (refs/commands).
 *  It holds a SINGLE rAF clock and performs exactly ONE buffer write per frame —
 *  multiple sources coalesce into that one write. Per-tick layer changes flow
 *  through updateTriggers diffs, never a full rebuild.
 *
 *  GPU specifics live behind {@link RenderBackend} (see backend.ts) so this file
 *  is typed against the seam, not deck.gl/MapLibre directly. The default WebGL2
 *  wiring (MapboxOverlay interleaved:true) is constructed lazily when a canvas is
 *  attached; this file is NOT unit-tested headlessly (needs WebGL) but the
 *  orchestration is exercised by injecting a fake backend in tests.
 */

import type {
  CanonicalEnvelope,
  EntityDelta,
  EntityFilter,
  EntityView,
  LayerHandle,
  LayerSpec,
  RenderEngine,
  RenderEvent,
  TransferableEntityBuffer,
  Unsubscribe,
  ViewState,
} from "../../contracts-ts/src/index.ts";
import type { EntityRef } from "../../contracts-ts/src/index.ts";

import { NullBackend, type RenderBackend } from "./backend.ts";
import { decodeOffThread, isTransferableBuffer, type BufferDecoder } from "./decode.ts";
import { EntityStore } from "./entity-store.ts";
import {
  ErrBadViewState,
  ErrLayerUnknown,
  ErrRenderBudgetExceeded,
} from "./errors.ts";
import { LayerManager } from "./layer-manager.ts";
import { planLod } from "./lod.ts";
import { TimeController } from "./time.ts";

type EngineEvent = "pick" | "hover" | "select" | "viewchange";

export interface RenderEngineOptions {
  /** Injected GPU backend; defaults to a NullBackend (headless/SSR-safe). */
  backend?: RenderBackend;
  /** Frame budget in ms before the engine degrades and emits ErrRenderBudgetExceeded. */
  frameBudgetMs?: number;
  /** GPU alloc cap for chunking decisions (bytes). */
  gpuAllocCapBytes?: number;
  /** Decoder for the binary fast path. */
  decoder?: BufferDecoder;
  /** Listener for non-fatal degrade signals (ErrRenderBudgetExceeded). */
  onBudgetExceeded?: (err: ErrRenderBudgetExceeded) => void;
  /** Scheduler for deferring subscriber fan-out OFF the render loop (test seam). */
  scheduleOffLoop?: (fn: () => void) => void;
  /** Staleness EXPIRE window in ms past `valid_time.stale` after which an entity is
   *  pruned from the render set. Default `Infinity` (ALL — never expire): staleness
   *  is a client-side filter the operator dials down, not a silent drop. */
  staleWindowMs?: number;
}

const DEFAULT_FRAME_BUDGET_MS = 1000 / 60; // 60fps target

function isFiniteView(v: ViewState): boolean {
  const finite =
    Number.isFinite(v.longitude) &&
    Number.isFinite(v.latitude) &&
    Number.isFinite(v.zoom) &&
    (v.pitch === undefined || Number.isFinite(v.pitch)) &&
    (v.bearing === undefined || Number.isFinite(v.bearing));
  if (!finite) return false;
  if (v.longitude < -180 || v.longitude > 180) return false;
  if (v.latitude < -90 || v.latitude > 90) return false;
  if (v.zoom < 0 || v.zoom > 24) return false;
  return true;
}

/** The headless render engine. Imperative, renderer-agnostic, React-free. */
export class Engine implements RenderEngine {
  private readonly store: EntityStore;
  private readonly layers: LayerManager;
  private readonly clock: TimeController;
  private readonly backend: RenderBackend;
  private readonly frameBudgetMs: number;
  private readonly gpuAllocCapBytes: number;
  private readonly decoder?: BufferDecoder;
  private readonly onBudgetExceeded?: (err: ErrRenderBudgetExceeded) => void;
  private readonly scheduleOffLoop: (fn: () => void) => void;

  private view: ViewState = { longitude: 0, latitude: 0, zoom: 0 };
  private readonly taps = new Map<EngineEvent, Set<(ev: RenderEvent) => void>>();
  /** Pending entity batches coalesced into the next single frame write. */
  private pendingFrameLayers = new Map<string, unknown[]>();
  private frameScheduled = false;

  constructor(opts: RenderEngineOptions = {}) {
    this.store = new EntityStore({ expireWindowMs: opts.staleWindowMs ?? Infinity });
    this.layers = new LayerManager();
    this.clock = new TimeController();
    this.backend = opts.backend ?? new NullBackend();
    this.frameBudgetMs = opts.frameBudgetMs ?? DEFAULT_FRAME_BUDGET_MS;
    this.gpuAllocCapBytes = opts.gpuAllocCapBytes ?? 1_000_000_000;
    this.decoder = opts.decoder;
    this.onBudgetExceeded = opts.onBudgetExceeded;
    // Default off-loop scheduler: microtask. NEVER calls back synchronously on
    // the render path. Tests can inject a synchronous one for determinism.
    this.scheduleOffLoop = opts.scheduleOffLoop ?? ((fn) => queueMicrotask(fn));
  }

  // --- Camera ---

  setView(view: ViewState): void {
    if (!isFiniteView(view)) {
      throw new ErrBadViewState(`bad view state: ${JSON.stringify(view)}`);
    }
    this.view = { ...view };
    this.backend.setView(this.view);
    this.fire("viewchange", { type: "viewchange", view: this.view });
  }

  getView(): ViewState {
    return { ...this.view };
  }

  // --- Layers (updateTriggers discipline) ---

  addLayer(spec: LayerSpec): LayerHandle {
    const handle = this.layers.add(spec); // throws ErrLayerUnknown if unsupported
    this.backend.createLayer(spec);
    return handle;
  }

  updateLayer(handle: LayerHandle, patch: Partial<LayerSpec>): void {
    const diff = this.layers.update(handle, patch); // throws ErrLayerUnknown
    if (diff.noop) return; // unchanged data MUST NOT rebuild
    const spec = this.layers.specFor(handle)!;
    this.backend.patchLayer(spec, {
      changedProps: diff.changedProps,
      changedTriggers: diff.changedTriggers,
      rebuild: diff.rebuild,
    });
  }

  removeLayer(handle: LayerHandle): void {
    const spec = this.layers.specFor(handle);
    this.layers.remove(handle); // throws ErrLayerUnknown
    if (spec) this.backend.destroyLayer(spec.id);
  }

  // --- Entity ingest (delta-by-id, off-thread binary path) ---

  upsertEntities(batch: CanonicalEnvelope[] | TransferableEntityBuffer): void {
    if (isTransferableBuffer(batch)) {
      // Binary fast path: decode OFF-THREAD; the main thread does not block.
      void decodeOffThread(batch, this.decoder).then((entities) => {
        this.applyUpsert(entities);
      });
      return;
    }
    this.applyUpsert(batch);
  }

  private applyUpsert(entities: CanonicalEnvelope[]): void {
    // Delta-by-id: same id updates in place (EntityStore.upsert dedupes by id).
    this.store.upsert(entities);
    this.scheduleFrame();
  }

  /** Explicitly remove entities by id (e.g. a policy re-filter hides an object).
   *  Produces remove deltas through the store and dirties the next single frame.
   *  Unknown ids are ignored. */
  removeEntities(ids: string[]): void {
    if (ids.length === 0) return;
    this.store.remove(ids);
    this.scheduleFrame();
  }

  // --- Subscriptions (fire OFF the render loop) ---

  subscribe(filter: EntityFilter, cb: (entities: EntityView) => void): Unsubscribe {
    // EntityStore fans out synchronously per emit. We COALESCE a burst of emits into ONE deferred
    // delivery per off-loop drain (dirty-flag + accumulated deltas): a bulk load of N one-entity
    // upserts delivers ONE final view, not N callbacks (so a size-only consumer emits status once,
    // not N times). The callback still NEVER runs inside the render frame (deferred off the loop).
    // With a SYNCHRONOUS scheduleOffLoop (test determinism) this degrades to per-emit delivery —
    // still correct, just uncoalesced.
    let latest: EntityView | null = null;
    let pendingDeltas: EntityDelta[] = [];
    let scheduled = false;
    const flush = (): void => {
      scheduled = false;
      const view = latest;
      const deltas = pendingDeltas;
      latest = null;
      pendingDeltas = [];
      if (!view) return;
      // Deliver the FINAL view (its size/entities getters read the live set at access time) with
      // the ACCUMULATED deltas — delegating getters preserve laziness (a size-only consumer never
      // forces the entities materialization).
      cb({
        deltas,
        get size(): number {
          return view.size;
        },
        get entities(): CanonicalEnvelope[] {
          return view.entities;
        },
      });
    };
    return this.store.subscribe(filter, (view) => {
      latest = view;
      if (view.deltas.length) for (const d of view.deltas) pendingDeltas.push(d);
      if (!scheduled) {
        scheduled = true;
        this.scheduleOffLoop(flush);
      }
    });
  }

  // --- Time (single clock) ---

  setTime(t: { now: number; window: [number, number]; playbackRate: number }): void {
    this.clock.set(t);
    // Advancing the clock can expire stale entities; that produces remove deltas
    // fanned out through the store, and dirties the next single frame write.
    this.store.setClock(t.now);
    this.scheduleFrame();
  }

  /** Set the staleness EXPIRE window (ms past `valid_time.stale`); `Infinity` = ALL
   *  (never expire). The operator's staleness-filter slider drives this: tightening
   *  prunes now-expired entities immediately, widening reveals them on next tick. */
  setStaleWindowMs(ms: number): void {
    this.store.setStaleConfig({ expireWindowMs: ms });
    this.scheduleFrame();
  }

  // --- Event taps (imperative, not React synthetic) ---

  on(event: EngineEvent, cb: (ev: RenderEvent) => void): Unsubscribe {
    let set = this.taps.get(event);
    if (!set) {
      set = new Set();
      this.taps.set(event, set);
    }
    set.add(cb);
    return () => {
      this.taps.get(event)?.delete(cb);
    };
  }

  private fire(event: EngineEvent, ev: RenderEvent): void {
    const set = this.taps.get(event);
    if (!set) return;
    for (const cb of set) cb(ev);
  }

  // --- Picking ---

  pick(screen: { x: number; y: number }): EntityRef | null {
    const hit = this.backend.pick(screen);
    if (!hit) return null;
    const ev: RenderEvent = { type: "pick", entity: { id: hit.id }, screen };
    this.fire("pick", ev);
    return { id: hit.id };
  }

  // --- Single rAF frame: ONE buffer write, coalesced, budget-aware ---

  /** Mark the engine dirty; the next tick writes exactly one coalesced buffer.
   *  Multiple upserts before a tick collapse into that single write. */
  private scheduleFrame(): void {
    this.frameScheduled = true;
  }

  /** Drive ONE frame. The host's rAF loop calls this once per animation frame.
   *  Coalesces all pending entity state into ONE backend write, advances the
   *  clock, and degrades (LOD/throttle) if the frame overran the budget.
   *  Returns true if a write happened. */
  tick(realDtMs = this.frameBudgetMs): boolean {
    if (!this.frameScheduled) return false;
    this.frameScheduled = false;

    this.clock.advance(realDtMs);
    const expired = this.store.setClock(this.clock.now());

    // Build per-layer data from the (single) current entity snapshot. This is
    // the ONE write per frame: all sources have already coalesced into the store.
    const snapshot = this.store.snapshot();
    this.pendingFrameLayers = new Map();
    for (const spec of this.layers.list()) {
      const data = snapshot
        .filter((s) => spec.kinds.includes(s.entity.kind) && s.staleness !== "expired")
        .map((s) => s.entity);
      // LOD/aggregation hook: choose resolution/chunking from the active zoom.
      planLod(spec, this.view.zoom, data.length, this.gpuAllocCapBytes);
      this.pendingFrameLayers.set(spec.id, data);
    }
    this.backend.writeFrame(this.pendingFrameLayers);

    // Budget evaluation: degrade gracefully, never freeze (## Behavioral Contracts).
    const frameMs = this.backend.lastFrameMs();
    if (frameMs > this.frameBudgetMs) {
      const err = new ErrRenderBudgetExceeded(frameMs, this.frameBudgetMs);
      this.onBudgetExceeded?.(err);
    }
    void expired;
    return true;
  }

  /** True if a frame write is pending (used by hosts that gate their rAF). */
  get dirty(): boolean {
    return this.frameScheduled;
  }

  dispose(): void {
    this.backend.dispose();
    this.taps.clear();
  }
}

/** Construct the engine. Without a backend it is headless-safe; the WebGL2
 *  backend (MapLibre v5 + deck.gl MapboxOverlay interleaved:true) is injected by
 *  the map client when a canvas exists. */
export function createRenderEngine(opts: RenderEngineOptions = {}): Engine {
  return new Engine(opts);
}
